import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { isTokenExpired } from '@/lib/helpers';
import { useAffiliateUserQuery } from '../queries';

export type AffiliateTokenStatus = 'valid' | 'missing' | 'expired' | 'error';

interface UseAffiliateTokenResult {
    /** The token from URL query params */
    token: string | null;
    /** Token validation status */
    status: AffiliateTokenStatus;
    /** Affiliate user data from the token */
    affiliateUser: ReturnType<typeof useAffiliateUserQuery>['data'];
    /** Whether the user data is loading */
    isLoading: boolean;
    /** Error from fetching user data */
    error: Error | null;
}

/**
 * Hook to handle affiliate registration token validation and user data fetching.
 * Extracts token from URL, validates expiration, and fetches affiliate user data.
 */
export function useAffiliateToken(): UseAffiliateTokenResult {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // Determine token status
    const status = useMemo<AffiliateTokenStatus>(() => {
        if (!token) return 'missing';
        if (isTokenExpired(token)) return 'expired';
        return 'valid';
    }, [token]);

    // Only fetch user data if token is valid
    const { data: affiliateUser, isLoading, error } = useAffiliateUserQuery(status === 'valid' ? token : null);

    return {
        token,
        status,
        affiliateUser,
        isLoading,
        error: error as Error | null,
    };
}
